import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { formatCurrency } from "../../../../utils/utils";
import { toast } from "sonner";
import {
  executeFetchPlan,
  fetchCacheShareRangesCerebro,
  fetchCacheShareRangesSportsradar,
  fetchCacheShareRangesSportsradarLive,
  fetchCacheShareRangesCacheTeams,
  fetchCacheShareRangesGeniusFootball,
  isCerebroTeam,
  isCacheTeam,
  isGeniusTeam,
} from "../services/cacheShareServices";
import { resolveFetchPlan, legacySportKey, SPORTS } from "../taxonomy";

// NCAA D-I basketball (mbb / wbb) routes to the live SR endpoint so coaches
// see current-season rosters / stats. Football still uses the cached SR
// endpoint because it has its own ingest pipeline. The legacy cached SR
// basketball collections are empty post-migration, so anything mbb/wbb
// MUST hit the live path.
const usesLiveSr = (sportValue) =>
  sportValue === "mbb" || sportValue === "wbb";

// Legacy sport-options array consumed by the Add Player modal etc. The new
// cascade decomposes (sport, gender) so the legacy callers see the combined
// key (mbb / wbb) they always expected.
export const sportOptions = [
  { value: "mbb", name: "Basketball (M)" },
  { value: "wbb", name: "Basketball (W)" },
  { value: "fb", name: "Football" },
];

// Normalize calculator result rows (any source) into the grid's athlete
// shape, payouts included. Cerebro returns `cerebroId` instead of
// `sportsRadarId`; football rows carry positionGroup/side for the filters.
const mapCalcResults = (rawResults) =>
  (rawResults || []).map((item) => ({
    avatar: item.profilePhoto || "",
    playerName: `${item.firstName || ""} ${item.lastName || ""}`.trim(),
    email: item.email || "",
    sportsRadarId: item.sportsRadarId || item.cerebroId || "",
    sportScore: item.sportScore || 0,
    cacheScore: item.cacheScore || 0,
    position: item.position || "",
    positionGroup: item.positionGroup || "",
    side: item.side || "",
    jersey: item.jersey || "",
    status: item.status || "",
    teamName: item.teamName || "",
    redshirtSeason: item.isPreviousSeason ?? item.redshirtSeason ?? false,
    isPreviousSeason: item.isPreviousSeason ?? item.redshirtSeason ?? false,
    seasonYear: item.seasonYear ?? null,
    isRegistered: item.isRegistered || false,
    athleteId: item.athleteId || "",
    cacheStatus: item.cacheStatus || null,
    payoutTeam: item.payoutTeam ?? null,
    payoutAllPlayers: item.payoutAllPlayers ?? null,
  }));

const EMPTY_FILTERS = {
  sport: null,         // base sport: { value: 'bb', name: 'Basketball', hasGender: true }
  gender: null,        // when sport.hasGender: { value: 'M' | 'F', name: '...' }
  tier: null,
  region: null,
  body: null,
  subDivision: null,
  classDivision: null, // Step 5b — State HS Association class/division pick
  conference: null,
  teamSearch: "",
};

// `allTeams` (default true): the team list keeps every saved team while a
// provider is paused, rather than only the teams with saved stats. Its live
// caller, the admin Team Codes panel, binds a code to a team and never
// scores it, so a football team without saved stats must stay pickable. A
// caller that scores the picked team passes allTeams: false.
export const useCacheShare = ({ team, allTeams = true }) => {
  // Default $0.00 (invalid on purpose): Estimate payouts stays disabled
  // until the user enters their own budget.
  const [revenueAmount, setRevenueAmount] = useState("0.00");
  const [budget, setBudget] = useState(0);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [showRanges, setShowRanges] = useState(false);
  const [athletes, setAthletes] = useState([]);
  const [addedPlayers, setAddedPlayers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingTeams, setIsLoadingTeams] = useState(false);
  const [allocatedAmount, setAllocatedAmount] = useState(0);
  const [remainingAmount, setRemainingAmount] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [recalculatedAthletes, setRecalculatedAthletes] = useState([]);
  const [selectedPlayersCount, setSelectedPlayersCount] = useState(0);
  const [selectedAddedPlayersCount, setSelectedAddedPlayersCount] = useState(0);
  const [isAddPlayerModalOpen, setIsAddPlayerModalOpen] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState({});
  const [cacheshareTeam, setCacheshareTeam] = useState({});
  // Cascade context for the team currently displayed in the ranges grid.
  // Snapshotted on Estimate Payouts so subsequent recalcs/imports keep
  // hitting the right Cerebro leagues even if the user has since changed
  // the cascade picker.
  const [cacheshareCerebroCtx, setCacheshareCerebroCtx] = useState({
    cerebroSports: [],
    leagueNames: [],
  });
  const [teams, setTeams] = useState([]);

  const gridApiRef = useRef(null);
  const addedPlayersGridApiRef = useRef(null);


  // Adapter exposed so legacy children (Add Player modal, header label) that
  // expected the old `selectedSport` object keep working. We compose the
  // legacy sport-key (mbb / wbb / fb / ...) from the new (sport, gender) pick.
  const selectedSport = useMemo(() => {
    if (!filters.sport) return {};
    const value = legacySportKey(filters.sport.value, filters.gender?.value);
    if (!value) return {};
    const name = filters.gender
      ? `${filters.sport.name} (${filters.gender.name})`
      : filters.sport.name;
    return { value, name };
  }, [filters.sport, filters.gender]);
  const setSelectedSport = useCallback(
    (next) => {
      // Legacy callers pass the combined key directly. Decompose it back into
      // the new (sport, gender) shape so the cascade stays consistent.
      if (!next) {
        setFilters(EMPTY_FILTERS);
        return;
      }
      const v = next.value;
      if (v === "mbb" || v === "wbb") {
        setFilters({
          ...EMPTY_FILTERS,
          sport: { value: "bb", name: "Basketball", hasGender: true },
          gender: { value: v === "wbb" ? "F" : "M", name: v === "wbb" ? "Women/Girls" : "Men/Boys" },
        });
        return;
      }
      if (v === "fb") {
        setFilters({
          ...EMPTY_FILTERS,
          sport: SPORTS.find((s) => s.value === "fb") || next,
        });
        return;
      }
      setFilters({ ...EMPTY_FILTERS, sport: next });
    },
    [],
  );

  const handleAmountChange = (e) => {
    const inputValue = e.target.value;

    // Allow empty input
    if (inputValue === "") {
      setRevenueAmount("");
      return;
    }

    // Format and set the value
    const formatted = formatCurrency(inputValue);
    setRevenueAmount(formatted);
  };

  // Cascade-driven team fetch. Whenever the user advances the cascade far
  // enough to resolve a fetch plan (sport + tier + body, plus region for
  // Pro/HS), we hit the right cache. Conference/sub-division narrowing is
  // applied client-side via the plan executor.
  const fetchPlan = useMemo(
    () =>
      resolveFetchPlan({
        sportValue: filters.sport?.value,
        gender: filters.gender?.value,
        tier: filters.tier?.value,
        region: filters.region?.value,
        body: filters.body,
        subDivision: filters.subDivision,
        classDivision: filters.classDivision,
        conference: filters.conference?.value,
        teamSearch: filters.teamSearch,
      }),
    [filters],
  );

  // Stringified plan key — re-fetch only when the *contents* of the plan
  // change, not on every parent rerender.
  const planKey = useMemo(
    () => (fetchPlan ? JSON.stringify(fetchPlan) : ""),
    [fetchPlan],
  );

  useEffect(() => {
    // Clear roster + payouts whenever the cascade meaningfully changes.
    setAthletes([]);
    setAddedPlayers([]);
    setShowRanges(false);
    setAllocatedAmount(0);
    setRemainingAmount(0);
    setBudget(0);
    setSelectedPlayersCount(0);
    setSelectedAddedPlayersCount(0);
    setCacheshareTeam({});
    setSelectedTeam(null);

    if (!fetchPlan) {
      setTeams([]);
      return;
    }

    let cancelled = false;
    setIsLoadingTeams(true);
    executeFetchPlan(fetchPlan, { allTeams })
      .then((result) => {
        if (cancelled) return;
        setTeams(result || []);
      })
      .catch((error) => {
        if (cancelled) return;
        console.error("Cascade team fetch failed:", error);
        toast.error("Failed to fetch teams for this selection.");
        setTeams([]);
      })
      .finally(() => {
        if (!cancelled) setIsLoadingTeams(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planKey]);


  // Clear roster data when team selection changes
  useEffect(() => {
    setAthletes([]);
    setAddedPlayers([]);
    setShowRanges(false);
    setAllocatedAmount(0);
    setRemainingAmount(0);
    setSelectedPlayersCount(0);
    setSelectedAddedPlayersCount(0);
  }, [selectedTeam]);

  const handleAmountBlur = () => {
    if (!revenueAmount || revenueAmount === "0") {
      return;
    }
    if (revenueAmount && !revenueAmount.includes(".")) {
      setRevenueAmount(revenueAmount + ".00");
    } else if (revenueAmount && revenueAmount.includes(".")) {
      const parts = revenueAmount.split(".");
      const decimalPart = parts[1] || "";
      if (decimalPart.length === 0) {
        setRevenueAmount(revenueAmount + "00");
      } else if (decimalPart.length === 1) {
        setRevenueAmount(revenueAmount + "0");
      }
    }
  };

  const getSelectClassNames = useMemo(
    () => ({
      control: () => "react-select-control",
      valueContainer: () => "react-select-value-container",
      input: () => "react-select-input",
      placeholder: () => "react-select-placeholder",
      singleValue: () => "react-select-single-value",
      indicatorSeparator: () => "react-select-indicator-separator",
      dropdownIndicator: () => "react-select-dropdown-indicator",
      menu: () => "react-select-menu",
      menuList: () => "react-select-menu-list",
      option: ({ isSelected, isFocused }) =>
        `react-select-option ${isSelected
          ? "react-select-option-selected"
          : isFocused
            ? "react-select-option-focused"
            : ""
        }`,
    }),
    []
  );

  useEffect(() => {
    const mainAthletesAllocation = athletes.reduce((sum, athlete) => {
      const payout =
        athlete.anticipatedPayout !== null &&
          athlete.anticipatedPayout !== undefined &&
          athlete.anticipatedPayout >= 0
          ? Number(athlete.anticipatedPayout)
          : Number(athlete.payoutTeam) || 0;
      return sum + payout;
    }, 0);

    const addedPlayersAllocation = addedPlayers.reduce((sum, player) => {
      const payout =
        player.anticipatedPayout !== null &&
          player.anticipatedPayout !== undefined &&
          player.anticipatedPayout >= 0
          ? Number(player.anticipatedPayout)
          : Number(player.payoutTeam) || 0;
      return sum + payout;
    }, 0);

    const totalAllocated = mainAthletesAllocation + addedPlayersAllocation;
    const remainingAmount = Number(budget) - totalAllocated;
    setAllocatedAmount(totalAllocated);
    setRemainingAmount(remainingAmount);
  }, [athletes, addedPlayers, budget]);

  const isButtonDisabled = useMemo(() => {
    const isInvalidAmount =
      !revenueAmount ||
      revenueAmount === "0" ||
      revenueAmount === "0.00" ||
      revenueAmount === "0.0";
    return isInvalidAmount || !team || !selectedSport.value || !selectedTeam?.id;
  }, [revenueAmount, team, selectedSport, selectedTeam]);

  const handleEstimatePayouts = useCallback(async () => {
    const budget = revenueAmount ? revenueAmount.replace(/,/g, "") : 0;
    setBudget(budget);
    setAddedPlayers([]);
    setSelectedPlayersCount(0);
    setSelectedAddedPlayersCount(0);
    setIsLoading(true);
    setShowRanges(true);
    setCacheshareTeam(selectedTeam);
    // Snapshot the current cascade context so subsequent recalcs hit the
    // same Cerebro leagues even if the user changes the cascade picker
    // after kicking off the calculation. Backend uses these to scope
    // per-player profile fetches against the right league set.
    // ctx feeds the backend's roster + per-player profile queries. Prefer
    // fetchPlan-derived values (cascade in play); fall back to whatever the
    // selected team carries (`cerebroLeague` is tagged onto every team by
    // executeFetchPlan AND can be set explicitly in coach-locked mode).
    // A team picked from a league list carries its exact squad scope
    // (`leagueKey`, "competition|division"); that beats the cascade's union
    // of keys so a 17U squad never rosters the whole program.
    const ctx = selectedTeam?.leagueKey
      ? { cerebroSports: [], leagueNames: [selectedTeam.leagueKey], gender: selectedSport?.value || "" }
      : {
        cerebroSports: fetchPlan?.cerebroSports?.length
          ? fetchPlan.cerebroSports
          : (selectedTeam?.cerebroLeague ? [selectedTeam.cerebroLeague] : []),
        leagueNames: fetchPlan?.leagueNames?.length
          ? fetchPlan.leagueNames
          : (selectedTeam?.leagueName ? [selectedTeam.leagueName] : []),
        gender: selectedSport?.value || "",
      };
    setCacheshareCerebroCtx(ctx);
    toast.success("Calculating ranges...");
    try {
      const revshareBudget = revenueAmount
        ? revenueAmount.replace(/,/g, "")
        : undefined;

      const useGenius = isGeniusTeam(selectedTeam);
      const useCache = !useGenius && isCacheTeam(selectedTeam);
      const useCerebro = !useGenius && !useCache && isCerebroTeam(selectedTeam);
      const teamDisplay = selectedTeam.name || selectedTeam.market || '';
      const liveSr =
        !useGenius && !useCache && !useCerebro && usesLiveSr(selectedSport?.value);
      const cacheShareRanges = useGenius
        ? await fetchCacheShareRangesGeniusFootball({
            rosterTeamId: selectedTeam.id || undefined,
            competitionId: selectedTeam.competitionId || undefined,
            revshareBudget: +revshareBudget,
          })
        : useCache
        ? await fetchCacheShareRangesCacheTeams({
            rosterTeamId: selectedTeam.id || undefined,
            revshareBudget: +revshareBudget,
          })
        : useCerebro
        ? await fetchCacheShareRangesCerebro({
            rosterTeamId: selectedTeam.id || undefined,
            teamName: teamDisplay,
            cerebroSports: ctx.cerebroSports,
            leagueNames: ctx.leagueNames,
            gender: ctx.gender,
            revshareBudget: +revshareBudget,
          })
        : liveSr
        ? await fetchCacheShareRangesSportsradarLive({
            rosterTeamId: selectedTeam.id || undefined,
            revshareBudget: +revshareBudget,
            sport: selectedSport.value,
          })
        : await fetchCacheShareRangesSportsradar({
            rosterTeamId: selectedTeam.id || undefined,
            revshareBudget: +revshareBudget,
            sport: selectedSport.value,
          });

      setAthletes(mapCalcResults(cacheShareRanges?.results));
    } catch (error) {
      console.error("Error fetching athlete data:", error);
      toast.error("Failed to calculate ranges. Please try again.");
      setIsLoading(false);
    } finally {
      setIsLoading(false);
      setTimeout(() => {
        const tableSection = document.getElementById("ranges-table");
        if (tableSection) {
          tableSection.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 100);
    }
  }, [
    setShowRanges,
    selectedSport,
    revenueAmount,
    setIsLoading,
    selectedTeam,
    fetchPlan,
  ]);

  const handleCellValueChanged = useCallback(
    (event) => {
      const updatedAthletes = athletes.map((athlete) => {
        if (athlete.sportsRadarId === event.data.sportsRadarId) {
          return {
            ...athlete,
            anticipatedPayout: event.newValue,
          };
        }
        return athlete;
      });
      setAthletes(updatedAthletes);
    },
    [athletes]
  );

  const handleAddedPlayersCellValueChanged = useCallback(
    (event) => {
      const updatedAddedPlayers = addedPlayers.map((player) => {
        if (player.sportsRadarId === event.data.sportsRadarId) {
          return {
            ...player,
            anticipatedPayout: event.newValue,
          };
        }
        return player;
      });
      setAddedPlayers(updatedAddedPlayers);
    },
    [addedPlayers]
  );

  const handleRecalculateForSelections = useCallback(
    async ({ athletes, importedAthletes }) => {
      if (
        (!athletes || athletes.length === 0) &&
        (!importedAthletes || importedAthletes.length === 0)
      ) {
        toast.error("Please select at least one kaio");
        return null;
      }

      toast.info(
        `Recalculating for ${athletes.length + importedAthletes.length
        } selected player${athletes.length + importedAthletes.length > 1 ? "s" : ""
        }...`
      );

      try {
        const revshareBudget = revenueAmount
          ? revenueAmount.replace(/,/g, "")
          : undefined;

        // Build an [{id, source}] list for cross-level imports by looking up
        // each selected import id against the `addedPlayers` state.
        const importedAthletesTagged = importedAthletes.map((id) => {
          const match = addedPlayers.find((p) => p.sportsRadarId === id);
          return {
            id,
            source: match?.source || "sportsradar",
            // Carry the search-computed data so uncached imported players
            // still get scored into the distribution.
            sportScore: match?.sportScore || 0,
            name: match?.playerName || "",
            position: match?.position || "",
            teamName: match?.teamName || "",
            // Source pipeline key (e.g. 'eyblmb') — the calculator uses it
            // to apply the cross-level penalty for players missing from
            // the cache_cb_* cache.
            sport: match?.sourceSport || "",
            // Genius (football) imports: origin team + competition.
            sourceTeamId: match?.sourceTeamId || "",
            sourceCompetitionId: match?.sourceCompetitionId || null,
          };
        });

        const useGenius = isGeniusTeam(cacheshareTeam);
        const useCache = !useGenius && isCacheTeam(cacheshareTeam);
        const useCerebro =
          !useGenius && !useCache && isCerebroTeam(cacheshareTeam);
        const liveSr =
          !useGenius && !useCache && !useCerebro &&
          usesLiveSr(selectedSport?.value);
        const taggedImports =
          importedAthletesTagged.length > 0 ? importedAthletesTagged : undefined;
        const cacheShareRanges = useGenius
          ? await fetchCacheShareRangesGeniusFootball({
              rosterTeamId: cacheshareTeam.id,
              competitionId: cacheshareTeam.competitionId || undefined,
              athleteIds: [...athletes, ...importedAthletes],
              importedAthleteIds: importedAthletes,
              importedAthletes: taggedImports,
              revshareBudget,
              selectedOnly: true,
            })
          : useCache
          ? await fetchCacheShareRangesCacheTeams({
              rosterTeamId: cacheshareTeam.id,
              athleteIds: [...athletes, ...importedAthletes],
              importedAthleteIds: importedAthletes,
              importedAthletes: taggedImports,
              revshareBudget,
              selectedOnly: true,
            })
          : useCerebro
          ? await fetchCacheShareRangesCerebro({
              rosterTeamId: cacheshareTeam.id,
              teamName: cacheshareTeam.name || cacheshareTeam.market || '',
              cerebroSports: cacheshareCerebroCtx.cerebroSports,
              leagueNames: cacheshareCerebroCtx.leagueNames,
              gender: cacheshareCerebroCtx.gender,
              athleteIds: [...athletes, ...importedAthletes],
              importedAthleteIds: importedAthletes,
              importedAthletes: taggedImports,
              revshareBudget,
              selectedOnly: true,
            })
          : liveSr
          ? await fetchCacheShareRangesSportsradarLive({
              rosterTeamId: cacheshareTeam.id,
              athleteIds: [...athletes, ...importedAthletes],
              importedAthleteIds: importedAthletes,
              importedAthletes: taggedImports,
              revshareBudget,
              selectedOnly: true,
              sport: selectedSport.value,
            })
          : await fetchCacheShareRangesSportsradar({
              rosterTeamId: cacheshareTeam.id,
              athleteIds: [...athletes, ...importedAthletes],
              importedAthleteIds: importedAthletes,
              importedAthletes: taggedImports,
              revshareBudget,
              selectedOnly: true,
              sport: selectedSport.value,
            });

        // Normalize cerebroId -> sportsRadarId so downstream lookups work.
        const results = (cacheShareRanges?.results || []).map((item) => ({
          ...item,
          sportsRadarId: item.sportsRadarId || item.cerebroId || "",
        }));
        const resultsById = {};
        results.forEach((item) => {
          resultsById[item.sportsRadarId] = item;
        });
        const allAthleteIds = [...athletes, ...importedAthletes];

        const recalculatedAthletes = allAthleteIds
          .map((sportsRadarId) => {
            const result = resultsById[sportsRadarId];
            if (!result) {
              return null;
            }
            // Find the original athlete data for fields the backend doesn't return
            const allCurrent = [...athletes, ...addedPlayers];
            const original = allCurrent.find(a => a.sportsRadarId === sportsRadarId) || {};

            return {
              sportsRadarId: result.sportsRadarId,
              email: result.email,
              firstName: result.firstName,
              lastName: result.lastName,
              playerName: `${result.firstName} ${result.lastName}`,
              avatar: result.profilePhoto || "",
              position: result.position || original.position || "",
              positionGroup: result.positionGroup || original.positionGroup || "",
              side: result.side || original.side || "",
              status: result.status || original.status || "",
              teamName: result.teamName || original.teamName || "",
              teamId: result.teamId,
              redshirtSeason: result.isPreviousSeason ?? result.redshirtSeason ?? original.redshirtSeason ?? false,
              isPreviousSeason: result.isPreviousSeason ?? result.redshirtSeason ?? original.isPreviousSeason ?? false,
              seasonYear: result.seasonYear ?? original.seasonYear ?? null,
              isRegistered: result.isRegistered ?? original.isRegistered ?? false,
              cacheScore: result.cacheScore,
              sportScore: result.sportScore,
              minCacheValue: result.minCacheValue,
              maxCacheValue: result.maxCacheValue,
              payoutTeam: result.payoutTeam ?? null,
              payoutAllPlayers: result.payoutAllPlayers ?? null,
              anticipatedPayout: result.payoutTeam ?? null,
              cacheShareTeam: result.cacheShareTeam,
              cacheShareAll: result.cacheShareAll,
              populationPercentile: result.populationPercentile,
            };
          })
          .filter((athlete) => athlete !== null);

        toast.success("Recalculation complete!");
        return recalculatedAthletes;
      } catch (error) {
        console.error("Error recalculating for selections:", error);
        toast.error("Failed to recalculate. Please try again.");
        return null;
      }
    },
    [revenueAmount, cacheshareTeam, cacheshareCerebroCtx, selectedSport, addedPlayers]
  );

  const handleGridReady = useCallback((params) => {
    gridApiRef.current = params.api;
  }, []);

  const handleSelectionChanged = useCallback(() => {
    if (!gridApiRef.current) return;
    const selectedRows = gridApiRef.current.getSelectedRows();
    setSelectedPlayersCount(selectedRows.length);
  }, []);

  const handleAddedPlayersGridReady = useCallback((params) => {
    addedPlayersGridApiRef.current = params.api;
  }, []);

  const handleAddedPlayersSelectionChanged = useCallback(() => {
    if (!addedPlayersGridApiRef.current) return;
    const selectedRows = addedPlayersGridApiRef.current.getSelectedRows();
    setSelectedAddedPlayersCount(selectedRows.length);
  }, []);

  const recalculateAllPayouts = useCallback(
    async (addedPlayersList, mainAthletesList) => {
      try {
        setIsLoading(true);

        const importedAthleteIds = [
          ...addedPlayersList.map((player) => player.sportsRadarId),
        ];
        // New shape: bundle each imported player with its origin so the
        // calculator can fetch + score HS recruits from Cerebro even when
        // the main roster is a SportRadar/NCAA team.
        const importedAthletes = addedPlayersList.map((player) => ({
          id: player.sportsRadarId,
          source: player.source || "sportsradar",
          // Search-computed data so the calculator can include players the
          // cache doesn't have yet (otherwise they're dropped from payouts).
          sportScore: player.sportScore || 0,
          name: player.playerName || "",
          position: player.position || "",
          teamName: player.teamName || "",
          // Source pipeline / catalogue key (e.g. 'eyblmb', a state
          // association class) so the calculator scores the player against
          // their own competitions and applies the cross-level cut.
          sport: player.sourceSport || "",
          // Genius (football) imports: origin team + competition so the
          // football calculator can pull the player's real stat line.
          sourceTeamId: player.sourceTeamId || "",
          sourceCompetitionId: player.sourceCompetitionId || null,
        }));

        const mainAthleteIds = [
          ...mainAthletesList.map((athlete) => athlete.sportsRadarId),
        ];

        const allAthleteIds = [...importedAthleteIds, ...mainAthleteIds];

        if (allAthleteIds.length === 0) {
          setIsLoading(false);
          return;
        }

        const revshareBudget = revenueAmount
          ? revenueAmount.replace(/,/g, "")
          : undefined;

        const useGenius = isGeniusTeam(cacheshareTeam);
        const useCache = !useGenius && isCacheTeam(cacheshareTeam);
        const useCerebro =
          !useGenius && !useCache && isCerebroTeam(cacheshareTeam);
        const liveSr =
          !useGenius && !useCache && !useCerebro &&
          usesLiveSr(selectedSport?.value);
        const taggedImports =
          importedAthletes.length > 0 ? importedAthletes : undefined;
        const cacheShareRanges = useGenius
          ? await fetchCacheShareRangesGeniusFootball({
              rosterTeamId: cacheshareTeam.id,
              competitionId: cacheshareTeam.competitionId || undefined,
              revshareBudget: +revshareBudget,
              importedAthleteIds:
                importedAthleteIds.length > 0 ? importedAthleteIds : undefined,
              importedAthletes: taggedImports,
              athleteIds: mainAthleteIds.length > 0 ? mainAthleteIds : undefined,
            })
          : useCache
          ? await fetchCacheShareRangesCacheTeams({
              rosterTeamId: cacheshareTeam.id,
              revshareBudget: +revshareBudget,
              athleteIds: mainAthleteIds.length > 0 ? mainAthleteIds : undefined,
              importedAthleteIds:
                importedAthleteIds.length > 0 ? importedAthleteIds : undefined,
              importedAthletes: taggedImports,
            })
          : useCerebro
          ? await fetchCacheShareRangesCerebro({
              rosterTeamId: cacheshareTeam.id,
              teamName: cacheshareTeam.name || cacheshareTeam.market || '',
              cerebroSports: cacheshareCerebroCtx.cerebroSports,
              leagueNames: cacheshareCerebroCtx.leagueNames,
              gender: cacheshareCerebroCtx.gender,
              revshareBudget: +revshareBudget,
              importedAthleteIds:
                importedAthleteIds.length > 0 ? importedAthleteIds : undefined,
              importedAthletes: taggedImports,
              athleteIds: mainAthleteIds.length > 0 ? mainAthleteIds : undefined,
            })
          : liveSr
          ? await fetchCacheShareRangesSportsradarLive({
              rosterTeamId: cacheshareTeam.id,
              revshareBudget: +revshareBudget,
              importedAthleteIds:
                importedAthleteIds.length > 0 ? importedAthleteIds : undefined,
              importedAthletes: taggedImports,
              athleteIds: mainAthleteIds.length > 0 ? mainAthleteIds : undefined,
              sport: selectedSport.value,
            })
          : await fetchCacheShareRangesSportsradar({
              rosterTeamId: cacheshareTeam.id,
              revshareBudget: +revshareBudget,
              importedAthleteIds:
                importedAthleteIds.length > 0 ? importedAthleteIds : undefined,
              importedAthletes: taggedImports,
              athleteIds: mainAthleteIds.length > 0 ? mainAthleteIds : undefined,
              sport: selectedSport.value,
            });

        const results = (cacheShareRanges?.results || []).map((item) => ({
          ...item,
          sportsRadarId: item.sportsRadarId || item.cerebroId || "",
        }));
        const resultsById = {};
        results.forEach((item) => {
          if (item.sportsRadarId) resultsById[item.sportsRadarId] = item;
        });

        const updatedAddedPlayers = addedPlayersList.map((player) => {
          const result = resultsById[player.sportsRadarId];
          if (result) {
            return {
              ...player,
              sportScore: result.sportScore ?? player.sportScore,
              cacheScore: result.cacheScore ?? player.cacheScore,
              redshirtSeason: result.isPreviousSeason ?? result.redshirtSeason ?? player.redshirtSeason,
              isPreviousSeason: result.isPreviousSeason ?? result.redshirtSeason ?? player.isPreviousSeason,
              seasonYear: result.seasonYear ?? player.seasonYear ?? null,
              payoutTeam: result.payoutTeam ?? null,
              payoutAllPlayers: result.payoutAllPlayers ?? null,
            };
          }
          return player;
        });

        const updatedMainAthletes = mainAthletesList.map((athlete) => {
          const result = resultsById[athlete.sportsRadarId];
          if (result) {
            return {
              ...athlete,
              sportScore: result.sportScore ?? athlete.sportScore,
              cacheScore: result.cacheScore ?? athlete.cacheScore,
              redshirtSeason: result.isPreviousSeason ?? result.redshirtSeason ?? athlete.redshirtSeason,
              isPreviousSeason: result.isPreviousSeason ?? result.redshirtSeason ?? athlete.isPreviousSeason,
              seasonYear: result.seasonYear ?? athlete.seasonYear ?? null,
              payoutTeam: result.payoutTeam ?? null,
              payoutAllPlayers: result.payoutAllPlayers ?? null,
            };
          }
          return athlete;
        });

        setAddedPlayers(updatedAddedPlayers);
        setAthletes(updatedMainAthletes);
      } catch (error) {
        console.error("Error recalculating payouts:", error);
        toast.error("Failed to recalculate payouts");
      } finally {
        setIsLoading(false);
      }
    },
    [revenueAmount, setIsLoading, selectedSport, cacheshareTeam, cacheshareCerebroCtx]
  );

  const handleRemoveAddedPlayers = useCallback(async () => {
    const selectedRows =
      addedPlayersGridApiRef.current?.getSelectedRows() || [];

    if (selectedRows.length === 0) {
      toast.error("Please select players to remove");
      return;
    }

    const selectedEmails = selectedRows.map((row) => row.email);
    const remainingAddedPlayers = addedPlayers.filter(
      (player) => !selectedEmails.includes(player.email)
    );

    setAddedPlayers(remainingAddedPlayers);

    if (remainingAddedPlayers.length > 0 || athletes.length > 0) {
      await recalculateAllPayouts(remainingAddedPlayers, athletes);
    }

    setSelectedAddedPlayersCount(0);
    toast.success(`Removed ${selectedRows.length} player(s) from roster`);
  }, [addedPlayers, athletes, recalculateAllPayouts]);

  const handleRecalculateClick = useCallback(async () => {
    setIsLoading(true);
    try {
      const mainGridSelections = gridApiRef.current?.getSelectedRows() || [];
      const addedPlayersSelections =
        addedPlayersGridApiRef.current?.getSelectedRows() || [];
      const importedAthletes = addedPlayersSelections.map(
        (row) => row.sportsRadarId
      );
      const athletesSelections = mainGridSelections.map(
        (row) => row.sportsRadarId
      );
      const allSelectedRows = [
        ...mainGridSelections,
        ...addedPlayersSelections,
      ];

      if (allSelectedRows.length === 0) {
        toast.error("Please select at least one player from the grid");
        return;
      }

      if (!gridApiRef.current) {
        toast.error("Grid not ready. Please try again.");
        return;
      }

      const recalculated = await handleRecalculateForSelections({
        athletes: athletesSelections,
        importedAthletes: importedAthletes,
      });

      if (recalculated) {
        setRecalculatedAthletes(recalculated);
        setIsModalOpen(true);
      }
    } catch (error) {
      toast.error("Failed to recalculate. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, [handleRecalculateForSelections]);

  const handleAddPlayers = useCallback(
    async (newPlayers) => {
      const playersToAdd = newPlayers.map((player) => ({
        avatar: player.avatar || "",
        playerName: player.playerName,
        email: player.email,
        sportsRadarId: player.sportsRadarId || "",
        position: player.position || "",
        status: player.status || "",
        sportScore: player.sportScore || 0,
        cacheScore: player.cacheScore || 0,
        redshirtSeason: player.redshirtSeason || false,
        teamName: player.teamName || "",
        cacheValue: player.cacheValue || "$0.00 - $0.00",
        payoutTeam: null,
        payoutAllPlayers: null,
        anticipatedPayout: null,
        // Preserve the data-source tag set by the Add Player modal so the
        // recalculate flow can build `importedAthletes: [{id, source}]` and
        // the backend fetches each imported player from the right API.
        // Dropping this causes Cerebro imports on NCAA rosters to be looked
        // up in SportRadar and come back without payouts.
        source: player.source || "sportsradar",
        // Genius (football) imports: origin team + competition so the
        // football calculator can pull the player's real stat line.
        sourceTeamId: player.sourceTeamId || "",
        sourceCompetitionId: player.sourceCompetitionId || null,
      }));

      const newAddedPlayers = [...addedPlayers, ...playersToAdd];

      await recalculateAllPayouts(newAddedPlayers, athletes);

      toast.success(`Added ${newPlayers.length} player(s) to roster`);
    },
    [addedPlayers, athletes, recalculateAllPayouts]
  );

  return {
    revenueAmount,
    setRevenueAmount,
    handleAmountChange,
    handleAmountBlur,
    isButtonDisabled,
    handleEstimatePayouts,
    selectedSport,
    setSelectedSport,
    sportOptions,
    showRanges,
    setShowRanges,
    athletes,
    addedPlayers,
    setAddedPlayers,
    isLoading,
    allocatedAmount,
    remainingAmount,
    budget: formatCurrency(budget),
    handleCellValueChanged,
    handleAddedPlayersCellValueChanged,
    handleRecalculateForSelections,
    handleRecalculateClick,
    handleGridReady,
    handleSelectionChanged,
    handleAddedPlayersGridReady,
    handleAddedPlayersSelectionChanged,
    handleRemoveAddedPlayers,
    selectedPlayersCount,
    selectedAddedPlayersCount,
    isModalOpen,
    setIsModalOpen,
    recalculatedAthletes,
    isAddPlayerModalOpen,
    setIsAddPlayerModalOpen,
    handleAddPlayers,
    team,
    teams,
    setSelectedTeam,
    selectedTeam,
    getSelectClassNames,
    cacheshareTeam,
    // Cascade filter state — consumed by CacheShareFilters.
    filters,
    setFilters,
    isLoadingTeams,
  };
};